/* A cell being edited in place: a text field over the cell, or for a choice a list of its
   choices lined up with the cell (a fixed layer, so the card's edge never clips it). Enter
   saves and moves down, Tab saves and moves right, Escape leaves the cell as it was. */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { cn } from "@/shared/lib/cn";

import type { SpaceColumn } from "@/shared/types";

export type Move = "down" | "right";

export default function CellEditor({ column, initial, onDraft, onDone }: {
  column: SpaceColumn;
  initial: string;
  /** What is typed so far, kept by the grid: a refetch moving the row keeps it. */
  onDraft?: (value: string) => void;
  /** `save`: keep the value; `move`: where the cursor goes after. */
  onDone: (save: boolean, value: string, move?: Move) => void;
}) {
  // An edit ends once: Enter, Tab or Escape unmounts the field, and its blur on the way out
  // must neither save a second time nor save what Escape dropped.
  const ended = useRef(false);
  const end = (save: boolean, value: string, move?: Move) => {
    if (ended.current) return;
    ended.current = true;
    onDone(save, value, move);
  };
  if (column.type === "choice") {
    return <ChoicePicker column={column} current={initial} onDone={end} />;
  }
  return <TextEditor column={column} initial={initial} onDraft={onDraft} onDone={end} />;
}

function TextEditor({ column, initial, onDraft, onDone }: {
  column: SpaceColumn;
  initial: string;
  onDraft?: (value: string) => void;
  onDone: (save: boolean, value: string, move?: Move) => void;
}) {
  const [value, setValue] = useState(initial);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const field = input.current;
    if (!field) return;
    field.focus({ preventScroll: true });
    field.setSelectionRange(field.value.length, field.value.length);
  }, []);
  return (
    <input
      ref={input}
      value={value}
      aria-label={column.name}
      inputMode={column.type === "number" ? "decimal" : undefined}
      onChange={(event) => {
        setValue(event.target.value);
        onDraft?.(event.target.value);
      }}
      onBlur={() => onDone(true, value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          onDone(true, value, "down");
        } else if (event.key === "Tab") {
          event.preventDefault();
          onDone(true, value, "right");
        } else if (event.key === "Escape") {
          event.preventDefault();
          onDone(false, initial);
        }
      }}
      className={cn(
        "w-full h-full px-3.5 bg-transparent outline-none text-[14px] text-[var(--dt-ink)]",
        column.type === "number" && "tabular-nums",
      )}
    />
  );
}

function ChoicePicker({ column, current, onDone }: {
  column: SpaceColumn;
  current: string;
  onDone: (save: boolean, value: string, move?: Move) => void;
}) {
  const anchor = useRef<HTMLSpanElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number; width: number } | null>(null);
  const choices = column.options.choices ?? [];

  // Lined up with the cell, below it unless the window ends first: then above.
  useLayoutEffect(() => {
    const align = () => {
      const cell = anchor.current?.closest("td")?.getBoundingClientRect();
      const height = list.current?.offsetHeight ?? 0;
      if (!cell) return;
      const below = window.innerHeight - cell.top - 4 >= height + 8;
      setPlace({
        left: cell.left + 6,
        top: below ? cell.top + 4 : Math.max(8, cell.bottom - 4 - height),
        width: Math.max(cell.width - 12, 160),
      });
    };
    align();
    window.addEventListener("scroll", align, true);
    window.addEventListener("resize", align);
    return () => {
      window.removeEventListener("scroll", align, true);
      window.removeEventListener("resize", align);
    };
  }, []);

  // A press anywhere else closes it, keeping the cell as it was.
  useEffect(() => {
    const away = (event: PointerEvent) => {
      if (!list.current?.contains(event.target as Node)) onDone(false, current);
    };
    window.addEventListener("pointerdown", away, true);
    return () => window.removeEventListener("pointerdown", away, true);
  }, [current, onDone]);

  return (
    <span ref={anchor} className="block">
      <div
        ref={list}
        role="listbox"
        aria-label={column.name}
        style={place ? { left: place.left, top: place.top, minWidth: place.width } : { visibility: "hidden" }}
        className="fixed z-50 grid gap-0.5 p-1 rounded-[12px] bg-[var(--dt-card)] ring-1 ring-[var(--dt-border)] shadow-menu fade-in"
      >
        {choices.map((choice, index) => (
          <button
            key={choice.name}
            type="button"
            role="option"
            aria-selected={choice.name === current}
            autoFocus={choice.name === current || (!current && index === 0)}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => onDone(true, choice.name)}
            onKeyDown={(event) => {
              const options = Array.from(list.current?.querySelectorAll("button") ?? []);
              const at = options.indexOf(event.currentTarget);
              if (event.key === "Escape") onDone(false, current);
              if (event.key === "ArrowDown") options[Math.min(options.length - 1, at + 1)]?.focus();
              if (event.key === "ArrowUp") options[Math.max(0, at - 1)]?.focus();
              if (event.key.startsWith("Arrow")) event.preventDefault();
            }}
            className={cn(
              "press h-[30px] px-2.5 rounded-[8px] text-left text-[13px] outline-none hover:bg-[var(--accent-hover)] focus-visible:bg-[var(--accent-hover)]",
              choice.name === current ? "text-[var(--dt-accent-ink)] font-medium" : "text-[var(--dt-text)]",
            )}
          >
            {choice.name}
          </button>
        ))}
      </div>
    </span>
  );
}
